import type { RegistrationStatus, Restaurant, Subscription, User } from "@/types";

export interface BackendSubscriptionResponse {
  stripeSubId?: string | null;
  status?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
}

export interface BackendRestaurantResponse {
  id: number;
  name: string;
  image?: string | null;
  color?: number | null;
  subscription?: BackendSubscriptionResponse | null;
}

export function backendColorToHex(color?: number | null): string | undefined {
  if (color === null || color === undefined) return undefined;

  return `#${color.toString(16).padStart(6, "0").slice(-6)}`;
}

export function hexToBackendColor(color?: string): number | undefined {
  if (!color) return undefined;

  return Number.parseInt(color.replace("#", ""), 16);
}

export function mapSubscriptionStatus(
  status?: string | null
): Subscription["status"] {
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      return "active";
    case "TRIALING":
      return "trialing";
    case "CANCELED":
      return "canceled";
    default:
      return "inactive";
  }
}

export function mapRestaurantResponse(
  restaurant: BackendRestaurantResponse
): Restaurant {
  return {
    id: restaurant.id,
    name: restaurant.name,
    image: restaurant.image ?? undefined,
    color: backendColorToHex(restaurant.color),
    subscription: {
      id: restaurant.subscription?.stripeSubId ?? undefined,
      startDate: restaurant.subscription?.currentPeriodStart ?? undefined,
      endDate: restaurant.subscription?.currentPeriodEnd ?? undefined,
      status: mapSubscriptionStatus(restaurant.subscription?.status),
    },
  };
}

export function deriveRegistrationStatus(
  user: User,
  restaurant?: Restaurant | null
): RegistrationStatus {
  if (!user.emailVerified) return "needsEmailVerification";

  const subscriptionStatus = restaurant?.subscription.status;
  if (subscriptionStatus === "active" || subscriptionStatus === "trialing") {
    return "complete";
  }

  return "needsSubscription";
}

export function jsonBlob(data: unknown): Blob {
  return new Blob([JSON.stringify(data)], { type: "application/json" });
}
